import type { ChatDto, MessageDto } from '@/core/data/chat/chat.interface';
import type { ChatRepository } from '@/core/data/chat/chat.repository';
import type { ApiError, Result } from '@/core/network/http/httpClient.interface';
import { generalError } from '@/core/network/http/httpError';

export class ChatService {
  private readonly repository: ChatRepository;

  constructor(repository: ChatRepository) {
    this.repository = repository;
  }

  async getChat(chatId: string): Promise<Result<ChatDto>> {
    try {
      const response = await this.repository.getChat(chatId);
      return response?.data ? { data: response.data } : { error: generalError() };
    } catch (error) {
      return {
        error: error as ApiError,
      };
    }
  }

  async createGambitChat(gambitId: string): Promise<Result<ChatDto>> {
    try {
      const response = await this.repository.createGambitChat(gambitId);
      return response?.data ? { data: response.data } : { error: generalError() };
    } catch (error) {
      return {
        error: error as ApiError,
      };
    }
  }

  async createFirestoreDoc(id: string): Promise<Result<any>> {
    try {
      const response = await this.repository.createFirestoreDoc(id);
      return response?.data ? response : { error: generalError() };
    } catch (error) {
      return {
        error: error as ApiError,
      };
    }
  }

  async markChatAsRead(chatId: string): Promise<void> {
    try {
      await this.repository.markChatAsRead(chatId);
    } catch {
      // fire-and-forget
    }
  }

  async getMessages(chatId: string, afterCursor?: string): Promise<Result<MessageDto[]>> {
    try {
      const response = await this.repository.getMessages(chatId, afterCursor);
      return response?.data ? { data: response.data, cursor: response.cursor } : { error: generalError() };
    } catch (error) {
      return {
        error: error as ApiError,
      };
    }
  }

  async postMessage(chatId: string, body: string): Promise<Result<MessageDto> | null> {
    try {
      const response = await this.repository.postMessage(chatId, body);
      return response?.data ? { data: response.data } : { error: generalError() };
    } catch (error) {
      return {
        error: error as ApiError,
      };
    }
  }

  async deleteMessage(chatId: string, messageId: string): Promise<Result<MessageDto> | null> {
    try {
      const response = await this.repository.deleteMessage(chatId, messageId);
      return response?.data ? { data: response.data } : { error: generalError() };
    } catch (error) {
      return {
        error: error as ApiError,
      };
    }
  }
}
